import { type FC, useCallback, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
  type FetchPageFn,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type AppDispatch } from "features/Application/globaltypes/redux";

import { createLicensesPacksFetcher } from "./Queries/queries";
import type { LicensesPackMetricsRow } from "./types";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";

import styles from "./packsReport.module.scss";

export type Props = {
  filter: PerformanceWithAccountFilter;
  onPackSelected: (row: LicensesPackMetricsRow) => void;
};

type ConnectedPropsType = ConnectedProps<typeof connector>;

export const PacksReportMetricsByPackTable: FC<Props & ConnectedPropsType> = ({
  filter,
  onPackSelected,
  setHasExportData,
}) => {
  const fetchPacksPage: FetchPageFn<LicensesPackMetricsRow> = useMemo(
    () => createLicensesPacksFetcher(filter),
    [filter],
  );
  const queryKeyBase = useMemo(() => ["LicensesPacks", filter] as const, [filter]);

  const columnOptions = useMemo<ColumnOption<LicensesPackMetricsRow>[]>(
    () => [
      {
        name: "Pack",
        width: 5,
        isSortable: true,
        sortKey: "PackName",
        render(entity) {
          return (
            <Tooltip
              target={
                <button className={styles.linkButton} onClick={() => onPackSelected(entity)}>
                  <TextTruncate className={styles.wordBreak}>{entity.PackName}</TextTruncate>
                </button>
              }
              content={entity.PackName}
              showAlways
            />
          );
        },
      },
      {
        name: "Customers",
        width: 3,
        isSortable: true,
        sortKey: "Customers",
        render: ({ Customers }) => Customers.toLocaleString(),
      },
      {
        name: "Licenses Issued",
        width: 3,
        isSortable: true,
        sortKey: "IssuedLicenses",
        render: ({ IssuedLicenses }) => IssuedLicenses.toLocaleString(),
      },
      {
        name: "Contract Licenses",
        width: 3,
        isSortable: true,
        sortKey: "ContractedLicenses",
        render: ({ ContractedLicenses }) => ContractedLicenses.toLocaleString(),
      },
    ],
    [onPackSelected],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This packs report contains no data.",
      }),
    [setHasExportData],
  );

  return (
    <GenericPerformanceListWithServerPagination
      columns={columnOptions}
      filter={filter}
      fetchPage={fetchPacksPage}
      queryKeyBase={queryKeyBase}
      hasDataForExport={updateExportDataState}
      isDateAllTime
    />
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);

const ConnectedComponent = connector(PacksReportMetricsByPackTable);
export default ConnectedComponent;
